import { gql, useMutation } from '@apollo/client';
import IconEdit from '@apollo/icons/default/IconEdit.svg';
import IconSuccess from '@apollo/icons/default/IconSuccess.svg';
import IconTime from '@apollo/icons/default/IconTime.svg';
import {
  Alert,
  Avatar,
  Button,
  Heading,
  Link,
  PopoverTrigger,
} from '@apollo/orbit';
import classnames from 'classnames';
import React, { useCallback, useMemo } from 'react';
import { Link as ReactRouterLink, useLocation } from 'react-router-dom';

import { proposalRouteConfig } from 'src/app/graph/routes';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { useIdentity } from 'src/hooks/useIdentity';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { assertUnreachableOrReturnDefault } from 'src/lib/assertUnreachable';
import Config from 'src/lib/config';
import { format } from 'src/lib/format';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { ProposalStatus, ReviewDecision } from 'src/lib/graphqlTypes/types';
import { setOverlayVisibleLocation } from 'src/lib/routing';

import { ReviewerMultiselectPopover } from './ReviewerMultiselectPopover';

export const RequestedReviewersSectionProposalFragment = gql`
  fragment RequestedReviewersSectionProposalFragment on Proposal {
    id
    status
    createdBy {
      id
    }
    requestedReviewers {
      user {
        id
        ... on User {
          fullName
          avatarUrl
        }
      }
      currentReview {
        id
        decision
        isDismissed
      }
    }
  }
`;

export const RequestedReviewersSectionServiceFragment = gql`
  fragment RequestedReviewersSectionServiceFragment on Service {
    id
    defaultProposalReviewers {
      __typename
      id
      ... on User {
        avatarUrl
        id
        fullName
      }
    }
    proposalsMustBeApprovedByADefaultReviewer
    minProposalApprovers
  }
`;

export const ProposalUpdateRequestedReviewersMutationGQL = gql`
  mutation ProposalUpdateRequestedReviewersMutation(
    $graphId: ID!
    $graphVariant: String!
    $input: UpdateRequestedReviewersInput!
  ) {
    graph(id: $graphId) {
      variant(name: $graphVariant) {
        proposal {
          ... on NotFoundError {
            message
          }
          ... on ValidationError {
            message
          }
          ... on PermissionError {
            message
          }
          ... on ProposalMutation {
            updateRequestedReviewers(input: $input) {
              ... on PermissionError {
                message
              }
              ... on ValidationError {
                message
              }
              ... on Proposal {
                id
                requestedReviewers {
                  user {
                    id
                    ... on User {
                      fullName
                      avatarUrl
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;
export const RequestedReviewersSection = ({
  proposal,
  graph,
  canEditProposal,
  membershipsData,
}: {
  proposal: GraphQLTypes.RequestedReviewersSectionProposalFragment;
  graph: GraphQLTypes.RequestedReviewersSectionServiceFragment;
  canEditProposal: boolean;
  membershipsData: GraphQLTypes.ReviewerMultiselectPopoverUsersServiceFragment | null;
}) => {
  const { me } = useIdentity();
  const location = useLocation();
  const { graphId, graphVariant } = useRouteParams(proposalRouteConfig);

  const selectedMemberIds = useMemo(
    () =>
      proposal.requestedReviewers
        .map((reviewer) => reviewer?.user?.id)
        .filter((id): id is NonNullable<typeof id> => !!id),
    [proposal.requestedReviewers],
  );

  const [updateReviewers] = useMutation<
    GraphQLTypes.ProposalUpdateRequestedReviewersMutation,
    GraphQLTypes.ProposalUpdateRequestedReviewersMutationVariables
  >(ProposalUpdateRequestedReviewersMutationGQL);

  const handleClose = useCallback(
    (selectedIds: string[]) => {
      const initialSelected = selectedMemberIds;
      const reviewerUserIdsToAdd = selectedIds.filter(
        (id) => !initialSelected.includes(id),
      );
      const reviewerUserIdsToRemove = initialSelected.filter(
        (id) => !selectedIds.includes(id),
      );
      return updateReviewers({
        variables: {
          graphId,
          graphVariant,
          input: {
            reviewerUserIdsToAdd,
            reviewerUserIdsToRemove,
          },
        },
      });
    },
    [graphId, graphVariant, selectedMemberIds, updateReviewers],
  );

  const defaultReviewerIds = useMemo(
    () =>
      graph.defaultProposalReviewers.flatMap((reviewer) => reviewer?.id ?? []),
    [graph.defaultProposalReviewers],
  );

  const reviewersWithReviewsIds = useMemo(
    () =>
      proposal.requestedReviewers
        .filter((reviewer) => reviewer?.currentReview !== null)
        .map((reviewer) => reviewer?.user?.id)
        .filter((id): id is NonNullable<typeof id> => !!id),
    [proposal.requestedReviewers],
  );

  const needToReconcileDefaultReviewers = useMemo(
    () =>
      proposal.status !== ProposalStatus.DRAFT &&
      proposal.status !== ProposalStatus.CLOSED &&
      proposal.status !== ProposalStatus.IMPLEMENTED &&
      !defaultReviewerIds.every((id) =>
        proposal.requestedReviewers.find((r) => r?.user?.id === id),
      ),
    [defaultReviewerIds, proposal.requestedReviewers, proposal.status],
  );

  return (
    <div>
      <div className="mb-3 flex items-center justify-between border-b border-b-primary pb-2">
        <Heading size="lg">Reviewers</Heading>
        {membershipsData ? (
          <ReviewerMultiselectPopover
            graph={membershipsData}
            selectedMemberIds={selectedMemberIds}
            reviewedMemberIds={reviewersWithReviewsIds}
            proposalAuthorId={proposal?.createdBy?.id}
            {...(graph.proposalsMustBeApprovedByADefaultReviewer
              ? {
                  mandatoryDefaultReviewerMemberIds: defaultReviewerIds,
                }
              : {})}
            onClose={handleClose}
            trigger={
              canEditProposal ? (
                <PopoverTrigger>
                  <Button variant="buttonLink" className="text-sm">
                    Manage reviewers
                  </Button>
                </PopoverTrigger>
              ) : (
                <></>
              )
            }
          />
        ) : (
          <Tooltip label="Loading members..." placement="top">
            <Button variant="buttonLink" className="text-sm">
              Manage reviewers
            </Button>
          </Tooltip>
        )}
      </div>
      <p className="mb-4 text-secondary">
        {proposal.requestedReviewers.length === 0 ? (
          <>
            No reviewers assigned yet. Please note that this proposal requires a
            minimum of{' '}
            <span className="font-semibold">{graph.minProposalApprovers}</span>{' '}
            {format.pluralize(graph.minProposalApprovers, 'approval')}
          </>
        ) : (
          <>
            This proposal requires a minimum of{' '}
            <span className="font-semibold">{graph.minProposalApprovers}</span>{' '}
            {format.pluralize(graph.minProposalApprovers, 'approval')}
          </>
        )}
        {graph.proposalsMustBeApprovedByADefaultReviewer ? (
          <>, with at least one approval coming from a default reviewer.</>
        ) : (
          <>.</>
        )}
      </p>
      <div className="mb-4 space-y-3">
        {proposal.requestedReviewers.map((review) => {
          const isDefaultReviewer = defaultReviewerIds.some(
            (id) => id === review?.user?.id,
          );

          return (
            <div
              key={review?.user?.id}
              className="flex items-center justify-between"
            >
              <div
                className={classnames(
                  'flex truncate text-base',
                  isDefaultReviewer ? 'items-start' : 'items-center',
                )}
              >
                {review?.user?.__typename === 'User' ? (
                  <>
                    <Avatar
                      size="sm"
                      name={review.user.fullName}
                      src={review.user.avatarUrl ?? undefined}
                      className="mr-2"
                    />
                    <div className="flex flex-col">
                      <span className="truncate">{review.user.fullName}</span>
                      {isDefaultReviewer && (
                        <div className="text-secondary">
                          {graph.proposalsMustBeApprovedByADefaultReviewer
                            ? 'Mandatory reviewer'
                            : 'Default reviewer'}
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <Avatar size="sm" className="mr-2" />
                    <div className="flex flex-col">
                      Unknown
                      {isDefaultReviewer && (
                        <div className="text-secondary">
                          {graph.proposalsMustBeApprovedByADefaultReviewer
                            ? 'Mandatory reviewer'
                            : 'Default reviewer'}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
              <div className="flex items-center">
                {review?.user?.id === me?.id && !!review?.currentReview && (
                  <Tooltip label="Edit review" placement="top">
                    <Button
                      size="sm"
                      leftIcon={<IconEdit />}
                      className="mr-2"
                      variant="hidden"
                      aria-label="Edit review"
                      as={ReactRouterLink}
                      to={setOverlayVisibleLocation({
                        location,
                        overlayName: Config.modals.addReview,
                      })}
                    />
                  </Tooltip>
                )}

                {!review?.currentReview ||
                review.currentReview.isDismissed ||
                review.currentReview.decision ===
                  ReviewDecision.NOT_APPROVED ? (
                  <Tooltip
                    label="Reviewer's decision is pending"
                    placement="top"
                  >
                    <IconTime
                      className="text-icon-primary"
                      aria-label="Reviewer's decision is pending"
                    />
                  </Tooltip>
                ) : review.currentReview.decision ===
                  ReviewDecision.APPROVED ? (
                  <Tooltip
                    label="Reviewer approved this proposal"
                    placement="top"
                  >
                    <IconSuccess
                      className="text-icon-success"
                      aria-label="Reviewer approved this proposal"
                    />
                  </Tooltip>
                ) : (
                  assertUnreachableOrReturnDefault(
                    review.currentReview.decision,
                    null,
                  )
                )}
              </div>
            </div>
          );
        })}
      </div>
      {needToReconcileDefaultReviewers && (
        <Alert status="info">
          <div className="flex items-center justify-between">
            <p className="mr-3">
              The list of default reviewers assigned to this graph has been
              updated.
            </p>
            <Link
              to={setOverlayVisibleLocation({
                location,
                overlayName: Config.modals.replaceRequestedReviewers,
              })}
            >
              View changes
            </Link>
          </div>
        </Alert>
      )}
    </div>
  );
};
